import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { getClinicPortalServiceTypeHeader } from '@/hooks/main/clinicPortalServiceType';
import type {
  VisitCheckFilters,
  VisitCheckResultResponse,
  VisitCheckStartResponse,
  VisitCheckStatusResponse,
} from '@/types/visitCheck.types';

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

function serviceTypeHeaders(): Record<string, string> {
  return { 'X-Service-Type': getClinicPortalServiceTypeHeader() };
}

/**
 * POST /api/v1/clinic/{site_type}/{clinic_number}/rezepts/visit-check
 * Searches matching rezept ids and dispatches the background job.
 */
export async function startVisitCheck(
  tenant: string,
  clinicNumber: string,
  filters: VisitCheckFilters
): Promise<VisitCheckStartResponse> {
  const res = await apiClient.post<ApiEnvelope<VisitCheckStartResponse>>(
    clinicApiPath(tenant, clinicNumber, '/rezepts/visit-check'),
    filters,
    { headers: serviceTypeHeaders() }
  );
  return res.data.data;
}

/**
 * GET /api/v1/clinic/{site_type}/{clinic_number}/rezepts/visit-check/{token}/status
 * Polled every 1s by VisitCheckProgressDialog while the job runs.
 */
export async function getVisitCheckStatus(
  tenant: string,
  clinicNumber: string,
  token: string
): Promise<VisitCheckStatusResponse> {
  const res = await apiClient.get<ApiEnvelope<VisitCheckStatusResponse>>(
    clinicApiPath(tenant, clinicNumber, `/rezepts/visit-check/${token}/status`)
  );
  return res.data.data;
}

/**
 * GET /api/v1/clinic/{site_type}/{clinic_number}/rezepts/visit-check/{token}/result
 * Final list of error rows shown on the result page.
 */
export async function getVisitCheckResult(
  tenant: string,
  clinicNumber: string,
  token: string
): Promise<VisitCheckResultResponse> {
  const res = await apiClient.get<ApiEnvelope<VisitCheckResultResponse>>(
    clinicApiPath(tenant, clinicNumber, `/rezepts/visit-check/${token}/result`)
  );
  return res.data.data;
}
